import {
  ShowBase,
  useShowContext,
  useGetList,
  useUpdate,
  useRefresh,
  useDelete,
  useNotify,
} from "ra-core";
import { Link, useNavigate } from "react-router";
import { useState } from "react";
import {
  ArrowLeft,
  Phone,
  Mail,
  MapPin,
  Briefcase,
  DollarSign,
  Hash,
  Activity,
  User,
  Users,
  Save,
  Pencil,
  X,
  Trash2,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

import type {
  Contact,
  Job,
} from "../../providers/fakerest/dataGenerator/types";
import { findStageLabel, findStageColor } from "../jobs/jobStages";
import {
  getContactSourceLabel,
  getContactSourceBadgeClass,
  getContactSourceIcon,
  getJobTypeLabel,
  getJobTypeBadgeClass,
  getJobTypeIcon,
  CONTACT_SOURCES,
} from "../constants";
import { AddressInput } from "../AddressInput";

export const ContactShow = () => {
  return (
    <ShowBase>
      <ContactShowContent />
    </ShowBase>
  );
};

const ContactShowContent = () => {
  const { record, isPending } = useShowContext<Contact>();
  const [deleteOne, { isPending: isDeleting }] = useDelete();
  const navigate = useNavigate();
  const notify = useNotify();

  const handleDelete = () => {
    deleteOne(
      "contacts",
      { id: record!.id, previousData: record },
      {
        onSuccess: () => {
          notify("Contact deleted successfully", { type: "success" });
          navigate("/contacts");
        },
        onError: () => {
          notify("Failed to delete contact", { type: "error" });
        },
      },
    );
  };

  if (isPending || !record) return null;

  return (
    <div className="p-4 max-w-4xl mx-auto">
      {/* Back button and delete */}
      <div className="flex items-center justify-between mb-4">
        <Link
          to="/contacts"
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Contacts
        </Link>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="destructive" size="sm" disabled={isDeleting}>
              <Trash2 className="h-4 w-4 mr-1" />
              Delete Contact
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Contact</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this contact? This action cannot
                be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      {/* Contact Info Card */}
      <ContactInfoCard contact={record} />

      {/* Associated Jobs */}
      <div className="mt-6">
        <ContactJobsList contactId={record.id} />
      </div>
    </div>
  );
};

const ContactInfoCard = ({ contact }: { contact: Contact }) => {
  const [update, { isPending: isUpdating }] = useUpdate();
  const refresh = useRefresh();
  const [isEditing, setIsEditing] = useState(false);

  // Editable fields
  const [name, setName] = useState(contact.name);
  const [phone, setPhone] = useState(contact.phone);
  const [email, setEmail] = useState(contact.email);
  const [address, setAddress] = useState(contact.address);
  const [source, setSource] = useState(contact.source);

  const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(contact.address)}`;

  const formattedValue = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(contact.total_value);

  const hasChanges =
    name !== contact.name ||
    phone !== contact.phone ||
    email !== contact.email ||
    address !== contact.address ||
    source !== contact.source;

  const handleSave = () => {
    if (!name.trim()) {
      alert("Name is required");
      return;
    }

    update(
      "contacts",
      {
        id: contact.id,
        data: {
          name: name.trim(),
          phone: phone.trim(),
          email: email.trim(),
          address: address.trim(),
          source,
        },
        previousData: contact,
      },
      {
        onSuccess: () => {
          setIsEditing(false);
          refresh();
        },
      },
    );
  };

  const handleCancel = () => {
    setName(contact.name);
    setPhone(contact.phone);
    setEmail(contact.email);
    setAddress(contact.address);
    setSource(contact.source);
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Edit Contact
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleCancel}>
                <X className="h-4 w-4 mr-1" />
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleSave}
                disabled={isUpdating || !hasChanges || !name.trim()}
              >
                <Save className="h-4 w-4 mr-1" />
                {isUpdating ? "Saving..." : "Save"}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Name */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <User className="h-4 w-4" />
              Name *
            </Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="John Doe"
            />
          </div>

          {/* Phone */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Phone className="h-4 w-4" />
              Phone
            </Label>
            <Input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="(555) 123-4567"
            />
          </div>

          {/* Email */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              Email
            </Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="john@example.com"
            />
          </div>

          {/* Address */}
          <div className="space-y-2">
            <Label>Address</Label>
            <AddressInput
              value={address}
              onChange={setAddress}
              placeholder="123 Main St, City, State 12345"
            />
          </div>

          {/* Source */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Source
            </Label>
            <Select value={source} onValueChange={setSource}>
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CONTACT_SOURCES.map((src) => (
                  <SelectItem key={src.id} value={src.id}>
                    <span className="flex items-center gap-2">
                      <src.icon className={`h-4 w-4 ${src.color}`} />
                      {src.label}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Read-only Stats */}
          <div className="flex flex-wrap items-center gap-4 pt-4 border-t text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <Hash className="h-4 w-4" />
              <span>Jobs:</span>
              <span className="font-semibold text-foreground">
                {contact.job_count}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              <span>Total Value:</span>
              <span className="font-semibold text-green-600 dark:text-green-400">
                {formattedValue}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-2xl">{contact.name}</CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsEditing(true)}
          >
            <Pencil className="h-4 w-4 mr-1" />
            Edit
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Contact details */}
        <div className="flex flex-wrap gap-4 text-sm">
          <a
            href={`tel:${contact.phone}`}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
          >
            <Phone className="h-4 w-4" />
            {contact.phone}
          </a>
          <a
            href={`mailto:${contact.email}`}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
          >
            <Mail className="h-4 w-4" />
            {contact.email}
          </a>
          <a
            href={googleMapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-muted-foreground hover:text-blue-600 dark:hover:text-blue-400"
          >
            <MapPin className="h-4 w-4" />
            {contact.address}
          </a>
        </div>

        {/* Stats row */}
        <div className="flex flex-wrap items-center gap-4 pt-2 border-t">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Source:</span>
            {(() => {
              const SourceIcon = getContactSourceIcon(contact.source);
              return (
                <Badge
                  className={`${getContactSourceBadgeClass(contact.source)} flex items-center gap-1.5`}
                >
                  <SourceIcon className="h-3 w-3" />
                  {getContactSourceLabel(contact.source)}
                </Badge>
              );
            })()}
          </div>
          <div className="flex items-center gap-2">
            <Hash className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Jobs:</span>
            <span className="font-semibold">{contact.job_count}</span>
          </div>
          <div className="flex items-center gap-2">
            <DollarSign className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Total Value:</span>
            <span className="font-semibold text-green-600 dark:text-green-400">
              {formattedValue}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const ContactJobsList = ({ contactId }: { contactId: number }) => {
  const navigate = useNavigate();

  const { data: jobs, isPending } = useGetList<Job>("jobs", {
    filter: { contact_id: contactId },
    sort: { field: "created_at", order: "DESC" },
    pagination: { page: 1, perPage: 50 },
  });

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(value);

  const handleRowClick = (jobId: number) => {
    navigate(`/jobs/${jobId}/show`);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Briefcase className="h-5 w-5" />
          Associated Jobs
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isPending ? (
          <p className="text-muted-foreground text-sm">Loading...</p>
        ) : jobs && jobs.length > 0 ? (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    <span className="flex items-center gap-1.5">
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      Address
                    </span>
                  </TableHead>
                  <TableHead>
                    <span className="flex items-center gap-1.5">
                      <Briefcase className="h-4 w-4 text-muted-foreground" />
                      Type
                    </span>
                  </TableHead>
                  <TableHead>
                    <span className="flex items-center gap-1.5">
                      <Activity className="h-4 w-4 text-muted-foreground" />
                      Status
                    </span>
                  </TableHead>
                  <TableHead className="text-right">
                    <span className="flex items-center gap-1.5 justify-end">
                      <DollarSign className="h-4 w-4 text-muted-foreground" />
                      Est. Value
                    </span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {jobs.map((job) => {
                  const JobTypeIcon = getJobTypeIcon(job.job_type);
                  return (
                    <TableRow
                      key={job.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => handleRowClick(job.id)}
                    >
                      <TableCell className="font-medium max-w-xs truncate">
                        {job.address}
                      </TableCell>
                      <TableCell>
                        <Badge
                          className={`${getJobTypeBadgeClass(job.job_type)} flex items-center gap-1.5 w-fit`}
                        >
                          <JobTypeIcon className="h-3 w-3" />
                          {getJobTypeLabel(job.job_type)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div
                            className={`w-2 h-2 rounded-full ${findStageColor(job.status)}`}
                          />
                          {findStageLabel(job.status)}
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(job.estimated_value)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        ) : (
          <p className="text-muted-foreground text-sm text-center py-4">
            No jobs associated with this contact
          </p>
        )}
      </CardContent>
    </Card>
  );
};
